import React from "react";
import {
  FaChevronRight,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaShieldAlt,
  FaWhatsapp,
} from "react-icons/fa";
import styled from "styled-components";
import logo from "../../images/logo_blank.png";
import map from "../../images/map.png";

const mapUrl =
  "https://www.google.com/maps/place/11°18'56.1%22N+75°52'50.9%22E/@11.3155364,75.8786388,18z/data=!4m4!3m3!8m2!3d11.3155785!4d75.880806?hl=en&entry=ttu&g_ep=EgoyMDI1MDExMC4wIKXMDSoASAFQAw%3D%3D";

const FooterContainer = styled.footer`
  position: relative;
  overflow: hidden;
  width: 100%;
  margin: 0;
  box-sizing: border-box;
  background: linear-gradient(90deg, #020b23 0%, #031b45 45%, #020b23 100%);
  color: #fff;
  font-family: inherit;

  &::before,
  &::after {
    content: "";
    position: absolute;
    bottom: -70px;
    width: 520px;
    height: 180px;
    border-radius: 50%;
    pointer-events: none;
    opacity: 0.72;
    background:
      radial-gradient(ellipse at center, transparent 48%, rgba(0, 194, 255, 0.2) 49%, transparent 50%),
      radial-gradient(ellipse at center, transparent 56%, rgba(0, 194, 255, 0.16) 57%, transparent 58%),
      radial-gradient(ellipse at center, transparent 64%, rgba(0, 119, 255, 0.14) 65%, transparent 66%),
      radial-gradient(ellipse at center, transparent 72%, rgba(0, 194, 255, 0.12) 73%, transparent 74%),
      radial-gradient(ellipse at center, transparent 80%, rgba(0, 119, 255, 0.1) 81%, transparent 82%);
  }

  &::before {
    left: -110px;
  }

  &::after {
    right: -110px;
  }
`;

const FooterMain = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  padding: 42px 64px 34px;
  display: grid;
  grid-template-columns: minmax(0, 1.12fr) minmax(330px, 0.95fr) minmax(0, 1.12fr);
  align-items: center;
  gap: 46px;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
    padding: 28px 16px 22px;
    gap: 12px;
  }

  @media (max-width: 600px) {
    padding: 20px 10px 16px;
    gap: 8px;
  }
`;

const FooterPanel = styled.div`
  min-height: 210px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-left: 1px solid rgba(190, 226, 255, 0.25);
  padding-left: 40px;

  @media (max-width: 1100px) {
    min-height: auto;
    padding-left: 10px;
    align-items: center;
    text-align: center;
  }

  @media (max-width: 600px) {
    padding-left: 6px;
    border-left: 1px solid rgba(190, 226, 255, 0.15);
  }
`;

const BrandPanel = styled(FooterPanel)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-left: 0;
  padding-left: 0;
  gap: 24px;

  @media (max-width: 1100px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const Logo = styled.img`
  width: 86px;
  height: auto;
  object-fit: contain;

  @media (max-width: 1100px) {
    width: 52px;
  }
`;

const BrandDivider = styled.span`
  width: 1px;
  height: 120px;
  flex-shrink: 0;
  display: block;
  background: rgba(190, 226, 255, 0.25);

  @media (max-width: 1100px) {
    display: none;
  }
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: #f8fbff;
  font-size: 16px;
  line-height: 1.25;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0;

  &::after {
    content: "";
    display: block;
    width: 42px;
    height: 2px;
    margin: 10px auto 12px;
    background: linear-gradient(135deg, #00c2ff, #0077ff);
    border-radius: 999px;
  }

  @media (max-width: 1100px) {
    font-size: 10px;
    letter-spacing: 0.5px;
  }
`;

const ContactList = styled.div`
  display: grid;
  gap: 18px;
`;

const ContactRow = styled.a`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
  color: #f5f8fd;
  text-decoration: none;
  font-size: 14px;
  line-height: 1.35;
  text-align: left;

  &:hover {
    color: #fff;
  }

  @media (max-width: 1100px) {
    font-size: 9px;
    gap: 6px;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const CircleIcon = styled.span`
  width: ${(props) => props.$size || "26px"};
  height: ${(props) => props.$size || "26px"};
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  color: #fff;
  background:
    radial-gradient(circle at 36% 30%, rgba(255, 255, 255, 0.28), transparent 28%),
    linear-gradient(135deg, #00c2ff, #0077ff);
  border: 1px solid rgba(0, 194, 255, 0.78);
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.18),
    0 0 26px rgba(0, 194, 255, 0.28);
  font-size: ${(props) => props.$fontSize || "13px"};
`;

const MapPanel = styled(FooterPanel)`
  align-items: center;
  padding-right: 40px;
  border-right: 1px solid rgba(190, 226, 255, 0.25);

  @media (max-width: 1100px) {
    border-right: 0;
    border-left: 0;
    padding-left: 0;
    padding-right: 0;
  }

  @media (max-width: 760px) {
    padding-right: 0;
  }
`;

const MapLink = styled.a`
  display: block;
  width: 100%;
  max-width: 430px;
  padding: 7px;
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(0, 194, 255, 0.22), rgba(255, 255, 255, 0.08));
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.08),
    0 0 34px rgba(0, 194, 255, 0.16);
`;

const MapImage = styled.img`
  display: block;
  width: 100%;
  height: 150px;
  object-fit: cover;
  border: 1px solid rgba(230, 241, 255, 0.32);
  border-radius: 13px;
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.34);

  @media (max-width: 1100px) {
    height: 80px;
  }

  @media (max-width: 600px) {
    height: 60px;
  }
`;

const MapCaption = styled.span`
  display: block;
  margin-top: 12px;
  color: rgba(248, 251, 255, 0.7);
  font-size: 12px;
  line-height: 1.4;
`;

const ConnectPanel = styled(FooterPanel)`
  align-items: center;
  text-align: center;
  border-left: 0;
  padding-left: 0;
`;

const SocialIcons = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin: 12px 0 22px;
`;

const SocialIconLink = styled.a`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 15px;
  text-decoration: none;
  background:
    radial-gradient(circle at 36% 28%, rgba(255, 255, 255, 0.08), transparent 34%),
    rgba(2, 11, 35, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow:
    inset 0 0 0 1px rgba(0, 194, 255, 0.16),
    inset 0 0 18px rgba(0, 194, 255, 0.08),
    0 0 20px rgba(0, 194, 255, 0.14);
  transition: transform 0.25s ease, border-color 0.25s ease, background 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(0, 194, 255, 0.45);
    background: linear-gradient(135deg, rgba(0, 194, 255, 0.32), rgba(0, 119, 255, 0.42));
    color: #fff;
  }

  @media (max-width: 1100px) {
    width: 26px;
    height: 26px;
    font-size: 11px;
  }
`;

const Cta = styled.a`
  width: min(100%, 390px);

  @media (max-width: 1100px) {
    display: none;
  }
  min-height: 66px;
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) 24px;
  align-items: center;
  gap: 12px;
  padding: 10px 16px 10px 12px;
  color: #fff;
  text-decoration: none;
  text-align: left;
  background:
    radial-gradient(circle at 9% 50%, rgba(0, 194, 255, 0.08), transparent 22%),
    linear-gradient(135deg, rgba(3, 27, 69, 0.94), rgba(2, 11, 35, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 16px;
  box-shadow:
    inset 0 0 0 1px rgba(0, 194, 255, 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 0 18px rgba(0, 194, 255, 0.1);

  &:hover {
    background:
      radial-gradient(circle at 9% 50%, rgba(0, 194, 255, 0.14), transparent 24%),
      linear-gradient(135deg, rgba(4, 37, 90, 0.96), rgba(2, 11, 35, 0.98));
    border-color: rgba(0, 194, 255, 0.38);
    color: #fff;
  }

  @media (max-width: 520px) {
    grid-template-columns: 34px minmax(0, 1fr) 24px;
    min-height: 60px;
    padding: 8px 10px;
  }
`;

const CtaText = styled.span`
  display: grid;
  gap: 4px;
  min-width: 0;
`;

const CtaSmall = styled.span`
  color: rgba(247, 250, 255, 0.78);
  font-size: 12px;
  line-height: 1.25;
`;

const CtaStrong = styled.span`
  color: #fff;
  font-size: 17px;
  line-height: 1.15;
  font-weight: 800;

  @media (max-width: 520px) {
    font-size: 16px;
  }
`;

const BottomBar = styled.div`
  position: relative;
  z-index: 1;
  padding: 10px 22px 12px;
  border-top: 1px solid rgba(198, 230, 255, 0.18);
  text-align: center;
  background: rgba(5, 18, 33, 0.42);
`;

const Copyright = styled.p`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 0;
  color: #f7f9fc;
  font-size: 12px;
  line-height: 1.5;
`;

const Shield = styled(FaShieldAlt)`
  color: #00c2ff;
  font-size: 14px;
  filter: drop-shadow(0 0 10px rgba(0, 194, 255, 0.35));
`;

const Footer = () => {
  return (
    <FooterContainer>
      <FooterMain>
        <BrandPanel>
          <Logo src={logo} alt="Arcus Builders and Developers" />
          <BrandDivider aria-hidden="true" />
          <div>
            <SectionTitle>Arcus Builders & Developers</SectionTitle>
            <ContactList>
              <ContactRow href={mapUrl} target="_blank" rel="noreferrer">
                <CircleIcon $size="20px" $fontSize="10px">
                  <FaMapMarkerAlt />
                </CircleIcon>
                <span>
                  door no 23/245C, Payambra Road,
                  <br />
                  Pantheerpadam, Kunnamangalam,
                  <br />
                  Kozhikode, Kerala - 673571
                </span>
              </ContactRow>
              <ContactRow href="tel:+919605063111">
                <CircleIcon $size="20px" $fontSize="10px">
                  <FaPhoneAlt />
                </CircleIcon>
                <span>+91 495 299 0455</span>
                <span>+91 9605063111</span>
                <span>+91 9605063222</span>
              </ContactRow>
            </ContactList>
          </div>
        </BrandPanel>

        <MapPanel>
          <SectionTitle>Find Us</SectionTitle>
          <MapLink href={mapUrl} target="_blank" rel="noreferrer">
            <MapImage src={map} alt="Arcus Builders location map" />
            <MapCaption>Payambra Road, Kunnamangalam, Kozhikode</MapCaption>
          </MapLink>
        </MapPanel>

        <ConnectPanel>
          <SectionTitle>Connect With Us</SectionTitle>
          <SocialIcons>
            <SocialIconLink
              href="https://www.facebook.com/arcusbuildersndevelopers"
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook"
            >
              <FaFacebookF />
            </SocialIconLink>
            <SocialIconLink
              href="https://www.instagram.com/arcus_builders?igsh=MXU1N2JoNXVlNndzYg=="
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
            >
              <FaInstagram />
            </SocialIconLink>
            <SocialIconLink
              href="https://in.linkedin.com/company/arcus-builders-and-developers"
              target="_blank"
              rel="noreferrer"
              aria-label="LinkedIn"
            >
              <FaLinkedinIn />
            </SocialIconLink>
            <SocialIconLink
              href="https://wa.me/919605063111"
              target="_blank"
              rel="noreferrer"
              aria-label="WhatsApp"
            >
              <FaWhatsapp />
            </SocialIconLink>
          </SocialIcons>

          <Cta href="tel:+919605063111" aria-label="Call Arcus Builders">
            <CircleIcon $size="32px" $fontSize="15px">
              <FaPhoneAlt />
            </CircleIcon>
            <CtaText>
              <CtaSmall>Have a project in mind?</CtaSmall>
              <CtaStrong>Let's Build Together</CtaStrong>
            </CtaText>
            <CircleIcon $size="22px" $fontSize="11px">
              <FaChevronRight />
            </CircleIcon>
          </Cta>
        </ConnectPanel>
      </FooterMain>

      <BottomBar>
        <Copyright>
          {/* <Shield /> */}
          <span>
            © ARCUS BUILDERS & DEVELOPERS 2026. All Rights Reserved.
          </span>
        </Copyright>
      </BottomBar>
    </FooterContainer>
  );
};

export default Footer;
